import { Check, Close } from '@mui/icons-material';
import { Alert, Box, FormControlLabel, Switch, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Invoice } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import { ConfidenceLevel } from 'utils/enums/Confidence';
import { getInvoiceHasConfidenceData } from 'utils/helpers/confidenceHelpers';
import { getReimbursementSummary } from 'utils/helpers/invoiceSectionSummaryHelpers';

import { InvoiceVerificationCard } from '../InvoiceVerificationCard';
import { useInlineReimbursementCreate } from './hooks/useInlineReimbursementCreate';
import { ReimbursementAutocomplete } from './ReimbursementAutocomplete';
import { ReimbursementDisplayCard } from './ReimbursementDisplayCard';

interface ReimbursementCardProps {
  currentInvoice: Invoice;
  confidenceLevel?: ConfidenceLevel;
  expanded?: boolean;
  onToggle?: () => void;
  onExpanded?: () => void;
  autoExpandReason?: string;
  hasError?: boolean;
}

export const ReimbursementCard = ({
  currentInvoice,
  confidenceLevel,
  expanded,
  onToggle,
  onExpanded,
  autoExpandReason,
  hasError
}: ReimbursementCardProps) => {
  const theme = useTheme();
  const { translate } = useTranslations();
  const { control, setValue, watch, trigger, clearErrors } = useFormContext();

  const isReimbursement = watch(InvoiceVerificationFieldName.IsReimbursement);
  const reimbursementCounterpartyId = watch(
    InvoiceVerificationFieldName.ReimbursementCounterpartyId
  );
  const reimbursementCounterpartyName = watch(
    InvoiceVerificationFieldName.ReimbursementCounterpartyName
  );

  const summary = useMemo(
    () =>
      getReimbursementSummary(
        {
          isReimbursement,
          reimbursementCounterpartyName
        },
        translate
      ),
    [isReimbursement, reimbursementCounterpartyName, translate]
  );

  const isReimbursementSelected = Boolean(reimbursementCounterpartyName);

  const isReimbursementValid =
    !isReimbursement || Boolean(reimbursementCounterpartyId);

  const hasConfidenceData = getInvoiceHasConfidenceData(currentInvoice);

  const checkmarkAdornment = useMemo(() => {
    const color = isReimbursementValid
      ? theme.palette.success.main
      : theme.palette.error.main;
    const Icon = isReimbursementValid ? Check : Close;

    return (
      <Box
        data-testid="reimbursement-checkmark"
        sx={{
          alignItems: 'center',
          backgroundColor: 'common.white',
          border: `1.5px solid ${color}`,
          borderRadius: '50%',
          display: 'flex',
          height: 20,
          justifyContent: 'center',
          width: 20
        }}
      >
        <Icon sx={{ color, fontSize: 14 }} />
      </Box>
    );
  }, [
    isReimbursementValid,
    theme.palette.error.main,
    theme.palette.success.main
  ]);

  const [isEditing, setIsEditing] = useState(false);
  const [createdReimbursementName, setCreatedReimbursementName] = useState('');

  const previousReimbursementIdRef = useRef<number | undefined>(
    reimbursementCounterpartyId
  );
  const justCreatedReimbursementRef = useRef(false);
  const wasReimbursement = useRef(isReimbursement);
  const clearedParticipant = useRef(false);

  const handleCreateSuccess = useCallback((name: string) => {
    justCreatedReimbursementRef.current = true;
    setCreatedReimbursementName(name);
    setIsEditing(false);
  }, []);

  const { isCreating, handleCreate } = useInlineReimbursementCreate(
    currentInvoice,
    handleCreateSuccess
  );

  useEffect(() => {
    if (isReimbursement) {
      if (clearedParticipant.current) {
        // react-hook-form re-applies defaultValues when the unmounted field
        // re-registers, resurrecting a participant the user cleared. Re-apply
        // the empty value so the field stays required until re-selected.
        setValue(
          InvoiceVerificationFieldName.ReimbursementCounterpartyId,
          undefined,
          { shouldValidate: true }
        );
        setValue(
          InvoiceVerificationFieldName.ReimbursementCounterpartyName,
          undefined
        );
      } else {
        trigger(InvoiceVerificationFieldName.ReimbursementCounterpartyId);
      }
    } else {
      clearErrors(InvoiceVerificationFieldName.ReimbursementCounterpartyId);
      // The reimbursement fields unmount when the toggle is switched off, but
      // react-hook-form keeps a false `isValid` from the stale required error,
      // so re-validate the remaining fields to restore the submit button.
      if (wasReimbursement.current) {
        trigger();
      }
    }
    wasReimbursement.current = isReimbursement;
  }, [isReimbursement, trigger, clearErrors, setValue]);

  useEffect(() => {
    if (isReimbursement && !isReimbursementSelected) {
      setIsEditing(true);
    }
  }, [isReimbursement, isReimbursementSelected]);

  useEffect(() => {
    if (
      reimbursementCounterpartyId !== previousReimbursementIdRef.current &&
      reimbursementCounterpartyId
    ) {
      setIsEditing(false);
      if (justCreatedReimbursementRef.current) {
        justCreatedReimbursementRef.current = false;
      } else {
        setCreatedReimbursementName('');
      }
    }
    previousReimbursementIdRef.current = reimbursementCounterpartyId;
  }, [reimbursementCounterpartyId]);

  const handleToggle = (checked: boolean) => {
    setValue(InvoiceVerificationFieldName.IsReimbursement, checked, {
      shouldDirty: true
    });

    if (!checked) {
      setCreatedReimbursementName('');
    }
  };

  const handleToggleEdit = () => {
    setCreatedReimbursementName('');
    setIsEditing((prev) => !prev);
  };

  return (
    <InvoiceVerificationCard
      title={translate('labels.reimbursement')}
      summary={summary}
      confidenceLevel={confidenceLevel}
      headerStartAdornment={
        confidenceLevel || !hasConfidenceData ? undefined : checkmarkAdornment
      }
      expanded={expanded}
      onToggle={onToggle}
      onExpanded={onExpanded}
      autoExpandReason={autoExpandReason}
      hasError={hasError}
    >
      <Box
        data-testid="reimbursement-card"
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Controller
          name={InvoiceVerificationFieldName.IsReimbursement}
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  {...field}
                  checked={field.value}
                  onChange={(event) => handleToggle(event.target.checked)}
                />
              }
              label={translate('labels.reimbursement')}
            />
          )}
        />

        {isReimbursement && (
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
              width: '100%'
            }}
          >
            {createdReimbursementName && (
              <Alert
                severity="success"
                data-testid="reimbursement-created-success"
                sx={{ width: '100%' }}
              >
                {translate('messages.reimbursementCreatedAndSelected', {
                  name: createdReimbursementName
                })}
              </Alert>
            )}
            {isEditing && (
              <Box sx={{ width: '100%' }}>
                <ReimbursementAutocomplete
                  currentInvoice={currentInvoice}
                  isCreating={isCreating}
                  onClear={() => {
                    clearedParticipant.current = true;
                  }}
                  onSelect={() => {
                    clearedParticipant.current = false;
                  }}
                  onCreateClick={(searchValue) => {
                    void handleCreate(searchValue);
                  }}
                />
              </Box>
            )}
            {!(isEditing && !isReimbursementSelected) && (
              <Box sx={{ width: '100%' }}>
                <ReimbursementDisplayCard
                  isEditing={isEditing}
                  onToggleEdit={handleToggleEdit}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </InvoiceVerificationCard>
  );
};
